import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@/contexts/NotificationContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Bell, 
  Calendar, 
  MessageSquare, 
  AlertCircle, 
  CheckCircle,
  Clock,
  Trash2,
  CheckCheck
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

const NotificationsPage = () => {
  const { i18n } = useTranslation();
  const {
    notifications,
    unreadCount,
    notificationsLoading: loading,
    notificationsLoadingMore,
    notificationsHasMore,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const getIcon = (type) => {
    switch (type) {
      case 'appointment': return <Calendar className="h-5 w-5 text-teal-500" />;
      case 'message': return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case 'success': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'alert': return <AlertCircle className="h-5 w-5 text-amber-500" />;
      default: return <Bell className="h-5 w-5 text-slate-500 dark:text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="notifications-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="h-8 w-8" />
              <div>
                <h1 className="text-3xl md:text-4xl font-bold">
                  {lang === 'ar' ? 'الإشعارات' : 'Notifications'}
                </h1>
                {unreadCount > 0 && (
                  <p className="text-teal-100 mt-1">
                    {lang === 'ar' 
                      ? `لديك ${unreadCount} إشعارات غير مقروءة`
                      : `You have ${unreadCount} unread notifications`
                    }
                  </p>
                )}
              </div>
            </div>
            {unreadCount > 0 && (
              <Button 
                variant="secondary" 
                size="sm"
                onClick={markAllAsRead}
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="h-4 w-4 mr-2" />
                {lang === 'ar' ? 'تحديد الكل كمقروء' : 'Mark all as read'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/4" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">
              {lang === 'ar' ? 'لا توجد إشعارات' : 'No notifications'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400">
              {lang === 'ar' 
                ? 'ستظهر هنا إشعاراتك عندما تتلقى تحديثات'
                : 'Your notifications will appear here when you receive updates'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <Card 
                key={notification.id}
                className={`border-0 shadow-sm transition-all ${
                  !notification.read ? 'bg-teal-50/50 border-l-4 border-l-teal-500' : ''
                }`}
                data-testid={`notification-${notification.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="p-2 rounded-full bg-slate-100">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className={`font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-700'}`}>
                            {notification.title}
                          </h3>
                          <p className="text-sm text-slate-600 mt-1">
                            {notification.message}
                          </p>
                        </div>
                        {!notification.read && (
                          <Badge className="bg-teal-500 shrink-0">
                            {lang === 'ar' ? 'جديد' : 'New'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="h-3 w-3" />
                          <span>
                            {formatDistanceToNow(new Date(notification.time), { addSuffix: true, locale })}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {!notification.read && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => deleteNotification(notification.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {notificationsHasMore && (
              <div className="flex justify-center pt-3">
                <Button
                  variant="outline"
                  onClick={loadMoreNotifications}
                  disabled={notificationsLoadingMore}
                >
                  {notificationsLoadingMore
                    ? (lang === 'ar' ? '\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...' : 'Loading...')
                    : (lang === 'ar' ? '\u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0645\u0632\u064a\u062f' : 'Load more')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
